import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import { AppContext } from "helpers/context";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RightPanel, Option } from "helpers/constants";

interface Props {
	selected: Option;
	onChange: (option: Option) => void;
	hazardLayerLength?: number;
	climateLayerLength?: number;
	eventLength?: number;
}

function Options(props: Props) {
	const [context] = useContext(AppContext);
	const [t] = useTranslation();

	const { hasHazard, hasClimate, hasEvent, isSingleMode, useModuleLabels } = useMemo(() => {
		const menu = context.menu || [];
		if (!menu.length) {
			return {
				hasHazard: false,
				hasClimate: false,
				hasEvent: false,
				isSingleMode: false,
				useModuleLabels: false
			};
		}

		const hasPermission = (id: string) =>
			menu.some((item) => item.id === id && item.status === "1");

		const isSingleMode =
			!!context.singleAnalyseModal ||
			(context.rightPanel === RightPanel.Single && !!context.singleLocationAnalysed);

		const isMultipleMode =
			!!context.multipleAnalyseModal ||
			((context.rightPanel === RightPanel.Multiple ||
				context.rightPanel === RightPanel.Events) &&
				!!context.multipleLocationsAnalysed);

		const hasEventPermission =
			(context.rightPanel === RightPanel.Multiple ||
				context.rightPanel === RightPanel.Events) &&
			hasPermission("104002");

		if (isSingleMode) {
			return {
				hasHazard: hasPermission("102001") || hasPermission("102004"),
				hasClimate: hasPermission("102002"),
				hasEvent: false,
				isSingleMode: true,
				useModuleLabels: true
			};
		}

		if (isMultipleMode) {
			return {
				hasHazard: hasPermission("103002") || hasPermission("103005"),
				hasClimate: hasPermission("103003"),
				hasEvent: hasEventPermission,
				isSingleMode: false,
				// 多点弹窗打开时使用与单点弹窗相同的模块级标签；结果面板使用直接类别名
				useModuleLabels: !!context.multipleAnalyseModal
			};
		}

		return {
			hasHazard: false,
			hasClimate: false,
			hasEvent: hasEventPermission,
			isSingleMode: false,
			useModuleLabels: false
		};
	}, [
		context.menu,
		context.singleAnalyseModal,
		context.singleLocationAnalysed,
		context.multipleAnalyseModal,
		context.multipleLocationsAnalysed,
		context.rightPanel
	]);

	return (
		<ToggleButtonGroup
			exclusive
			size="small"
			value={props.selected}
			onChange={(_, v) => { if (v !== null) props.onChange(v as Option); }}
			sx={{
				border: "1px solid var(--gray-border)",
				borderRadius: "4px",
				"& .MuiToggleButton-root": {
					color: "#333",
					textTransform: "uppercase",
					fontSize: "14px",
					fontWeight: 500,
					padding: "10px",
					border: "none",
					borderRadius: 0,
				},
				"& .MuiToggleButton-root:not(:first-of-type)": {
					borderLeft: "1px solid var(--gray-border)",
					marginLeft: 0,
				},
				"& .MuiToggleButton-root.Mui-selected": {
					color: "var(--green)",
					backgroundColor: "#0e8a3f14",
				},
				"& .MuiToggleButton-root.Mui-selected:hover": {
					color: "var(--green)",
				},
			}}
		>
			{hasHazard && (
				<ToggleButton value={Option.HAZARD} sx={{ fontSize: "12px!important;", padding: "10px 8px!important;" }}>
					{t("options.single.naturalHazards")}
					{props.hazardLayerLength ? `(${props.hazardLayerLength})` : ""}
				</ToggleButton>
			)}
			{hasClimate && (
				<ToggleButton value={Option.CLIMATE} sx={{ fontSize: "12px!important;", padding: "10px 8px!important;" }}>
					{t("options.single.climateChange")}
					{props.climateLayerLength ? `(${props.climateLayerLength})` : ""}
				</ToggleButton>
			)}
			{!isSingleMode && hasEvent && (
				<ToggleButton value={Option.EVENT} sx={{ fontSize: "12px!important;", padding: "10px 8px!important;" }}>
					{t("options.event")}{props.eventLength ? `(${props.eventLength})` : ""}
				</ToggleButton>
			)}
		</ToggleButtonGroup>
	);
}

export default Options;

